import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { toast, ToastContainer } from 'react-toastify';
import { CustomerContext } from '../../../contexts/CustomerContext';
import useQueryString from '../../../hooks/use-query-string';
import useLinkAttribution from '../../../hooks/useLinkAttribution';
import usePastImages from '../../../hooks/usePastImages';
import usePastImagesQuickOrderPricelist from '../../../hooks/usePastImagesQuickOrderPricelist';
import { useEmailInterceptor } from '../../EmailInterceptor/EmailInterceptor';
import { OrderPackage } from '../../../models';
import Sheet from '../../../models/Sheet';
import Spinner from '../../Spinner';
import { routes } from '../../../routes';
import QueryKeys from '../../../utils/QueryKeys';
import PastImageOption from './PastImageOption';
import DiscountPopup from './DiscountPopup';
import styles from './PastImagesQuickOrder.module.scss';
import { refreshVolumeDiscount } from '../../../api/carts';

// Mirrors Coupon::VOLUME_DISCOUNT_TIERS server-side. Used only for the live
// preview pill in the footer — actual discount math is computed by the API.
function volumeDiscountPercentage(count) {
  if (count < 2) return 0;
  if (count >= 5) return 40;
  return (count - 1) * 10;
}

function priceForOption(opt) {
  const priceRecord = opt.prices && opt.prices[0];
  return (priceRecord && Number(priceRecord.price)) || 0;
}

export default function PastImagesQuickOrder() {
  const { customer, setCustomer } = useContext(CustomerContext);
  const history = useHistory();
  const queryClient = useQueryClient();
  const { toReview } = useQueryString();
  const isUpsell = !!toReview;
  const cartKey = customer.priorityCart ? 'priorityCart' : 'cart';
  const cart = customer[cartKey];
  // Quick-order campaign links carry message_id; keep the attribution.
  useLinkAttribution(cart);
  const student = customer.currentStudent;
  const schoolId = student?.schoolId || customer.currentSchool?.id;

  const {
    data: pastImages,
    isLoading: isLoadingImages,
  } = usePastImages({
    studentId: student?.id,
    additionalQuery: cart ? { folder: { not_already_selected_for_cart: cart.id } } : {},
  });

  const {
    data: pricelist,
    isLoading: isLoadingPricelist,
  } = usePastImagesQuickOrderPricelist(schoolId);

  // WHAT "View Packages" OPENS: this year's pricelist for that photo's kind of
  // picture, decided server-side and carried on the photo itself
  // (`currentTypePackageId`, StudentImage#current_type_package). Separate from
  // the Quick Order pricelist above: that one sells three single-item options
  // per photo, this one sells the whole package lineup for the same photo. A
  // parent who wants a package rather than one 8x10 had no way off this screen
  // before (Skylar, 2026-09-13).
  //
  // It used to be a client-side substring match against the four hand-made
  // "Past Images" pricelists, which sent every "sportsfall2026" team photo to
  // the FALL PORTRAITS pricelist, and left "sportswinter2026" with no link at
  // all. It now rides along with the images themselves, so there is no second
  // request to wait for and no "is this a sport season" rule on this side.
  const { intercept } = useEmailInterceptor();

  const isFetching = useIsFetching();
  const [selections, setSelections] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasShownDiscountPopup, setHasShownDiscountPopup] = useState(false);
  const [isDiscountPopupOpen, setIsDiscountPopupOpen] = useState(false);

  // Only show options that have a configured price for the current school —
  // priceless options are admin work-in-progress and should be hidden from
  // customers (matches the behavior elsewhere in Purchase Pictures).
  const options = (pricelist?.options || []).filter((opt) => priceForOption(opt) > 0);
  const backgrounds = pricelist?.backgroundSet?.backgrounds || [];
  const packagePoses = pricelist?.packagePoses || [];

  // For each past student image, the admin-configured (pose-number, season)
  // mapping in package_poses gives us the right default background. If no
  // mapping exists we fall back to the first background in the package's set.
  const defaultsByStudentImageId = useMemo(() => {
    const map = {};
    (pastImages || []).forEach((si) => {
      const primaryPose = (si.poses && si.poses[0]) || null;
      const number = primaryPose?.number;
      const season = si.folder;

      const match = packagePoses.find((pp) => (
        pp.poseNumber === number && pp.season === season
      ));

      const bg = (match && match.background) || backgrounds[0] || null;
      map[si.id] = bg;
    });
    return map;
  }, [pastImages, packagePoses, backgrounds]);

  const handleSelectOption = (studentImageId, optionId) => {
    setSelections((prev) => {
      const next = { ...prev };
      if (!optionId) {
        delete next[studentImageId];
        return next;
      }
      const existing = next[studentImageId] || {};
      const defaultBg = defaultsByStudentImageId[studentImageId];
      next[studentImageId] = {
        optionId,
        backgroundId: existing.backgroundId || defaultBg?.id || backgrounds[0]?.id || null,
      };
      return next;
    });
  };

  // Leave the Quick Order screen for that photo's full package lineup. Mirrors
  // the legacy prior-season pitch exactly (PreviousImages v2 `handleContinue`):
  // stamp the photo's season so the builder resolves poses against THAT year,
  // then open the pricelist on its package-picking step. Behind the same email
  // interceptor the pitch uses, so the capture point doesn't move.
  //
  // The season sticks when the shopper has entered any access code OF THIS
  // STUDENT — Pose.authorized_entry_season, widened 2026-10-14 for exactly this
  // flow. Without it the builder falls back to the pricelist's own season, and
  // since that pricelist is now THIS YEAR'S, the parent would be shown this
  // year's photograph instead of the past one they just clicked.
  const handleViewPackages = (studentImage, packageId) => {
    if (!packageId) return;

    intercept(() => {
      localStorage.setItem(`currentSeason-${studentImage.studentId}`, studentImage.folder);
      history.push(routes.elementaryMultiPoseBuilder(packageId, { step: 'select-package' }));
    });
  };

  const handleSelectBackground = (studentImageId, backgroundId) => {
    setSelections((prev) => {
      const existing = prev[studentImageId];
      if (!existing) return prev;
      return { ...prev, [studentImageId]: { ...existing, backgroundId } };
    });
  };

  // Show the volume-discount popup once, the moment the customer makes their
  // first selection on the page. Resets on a fresh visit.
  useEffect(() => {
    if (hasShownDiscountPopup) return;
    if (Object.keys(selections).length === 0) return;
    setIsDiscountPopupOpen(true);
    setHasShownDiscountPopup(true);
  }, [selections, hasShownDiscountPopup]);

  const summary = useMemo(() => {
    const entries = Object.entries(selections);
    const total = entries.reduce((acc, [, sel]) => {
      const opt = options.find((o) => o.id === sel.optionId);
      return acc + priceForOption(opt || {});
    }, 0);
    return { count: entries.length, total };
  }, [selections, options]);

  const buildSheetsForOption = (option, pose, backgroundId) => {
    const imageTypes = option.imageTypes || [];
    const flat = imageTypes
      .map((imageType) => new Array(imageType.count || 1).fill(imageType))
      .reduce((acc, arr) => acc.concat(arr), []);

    return flat.map((imageType, index) => new Sheet({
      index,
      poseId: pose.id,
      pose,
      imageTypeId: imageType.id,
      imageType,
      backgroundId: imageType.requiresBg === false ? null : backgroundId,
    }));
  };

  const handleContinue = async () => {
    if (!cart) return;

    // ⚠️ AN EMPTY SELECTION IS A VALID CHOICE, IN EVERY MODE (Katie,
    // 2026-10-14). This used to hold the customer on the page -- the button was
    // disabled with nothing ticked unless they had arrived in upsell mode
    // (?toReview=true) -- so a parent who wanted none of these old photographs
    // had no way forward from the screen their access code had landed them on.
    // "No thank you" is an answer, and the gifts page has always taken it.
    // Same destination the upsell path already used: /c/review, with whatever
    // is already in their cart.
    if (summary.count === 0) {
      history.push(routes.review());
      return;
    }

    if (!pricelist) return;
    setIsSubmitting(true);

    try {
      const entries = Object.entries(selections);

      // serial saves: matches the way other builders create order packages,
      // and avoids racing the same cart_id through parallel writes.
      // eslint-disable-next-line no-restricted-syntax
      for (const [studentImageId, sel] of entries) {
        const studentImage = (pastImages || []).find((si) => si.id === studentImageId);
        const option = options.find((o) => o.id === sel.optionId);
        if (!studentImage || !option) {
          // eslint-disable-next-line no-continue
          continue;
        }

        const pose = (studentImage.poses && studentImage.poses[0]) || null;
        if (!pose) {
          // eslint-disable-next-line no-continue
          continue;
        }

        const resolvedBgId = sel.backgroundId
          || defaultsByStudentImageId[studentImage.id]?.id
          || backgrounds[0]?.id
          || null;
        const sheets = buildSheetsForOption(option, pose, resolvedBgId);

        // Do NOT set studentImageId / studentImage here. Api::StudentImageResource
        // declares has_many :order_packages with always_include_resource_ids,
        // which means a populated studentImage on an OrderPackage creates a
        // circular reference (orderPackage.studentImage.orderPackages[0] ===
        // orderPackage) the moment /c/review tries JSON.stringify(customer.cart).
        // The standard MultiPosePackageBuilder save avoids this by letting the
        // linkage flow through each Sheet's pose_id -> Pose -> student_image.
        const orderPackage = new OrderPackage({
          cartId: cart.id,
          studentId: student.id,
          optionId: option.id,
          packageId: pricelist.id,
          apiOption: option,
          extraPoses: 0,
          sheets,
          isPreviewHidden: false,
        });

        // eslint-disable-next-line no-await-in-loop
        await orderPackage.save({ with: 'sheets' });

        if (Object.keys(orderPackage.errors || {}).length > 0) {
          throw new Error('OrderPackage save validation error');
        }
      }

      // Compute and attach the tiered volume-discount coupon (server-side
      // is the source of truth — the footer pill is just a preview).
      try {
        await refreshVolumeDiscount(cart.id);
      } catch (couponErr) {
        // Don't block checkout on a discount failure; the customer can
        // re-trigger by editing the cart, and we surface it in Sentry.
        // eslint-disable-next-line no-console
        console.error('PastImagesQuickOrder: refreshVolumeDiscount failed', couponErr);
      }

      queryClient.invalidateQueries({ queryKey: [QueryKeys.cart] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.numItems] });

      setCustomer({ ...customer });
      history.push(routes.review());
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('PastImagesQuickOrder: save failed', err);
      toast('There was an error adding these items to your cart. Please refresh and try again.', { type: 'error' });
      setIsSubmitting(false);
    }
  };

  if (!student) {
    // Without a current student we can't render. Send them to the entry form.
    history.replace(routes.studentIdEntry());
    return null;
  }

  // A tile is painted once, with its "View Packages" link already decided: the
  // link rides on the image, so the images landing IS the link landing.
  if (isLoadingImages || isLoadingPricelist
      || (isFetching && (pastImages || []).length === 0)) {
    return (
      <div className={styles.container}>
        <div className={styles.spinnerWrap}><Spinner color="blue" /></div>
      </div>
    );
  }

  if (!pricelist) {
    // Upsell mode: nothing to offer, pass through to checkout silently.
    if (isUpsell) {
      history.replace(routes.review());
      return null;
    }
    return (
      <div className={styles.container}>
        <div className={styles.inner}>
          <h1 className={styles.header}>Pictures from Previous School Years</h1>
          <div className={styles.emptyState}>
            This option isn’t available right now. Please check back soon.
          </div>
        </div>
      </div>
    );
  }

  if (!pastImages || pastImages.length === 0) {
    // Upsell mode: customer has nothing to add from past years, send them
    // to the cart with their current-year purchase already in it.
    if (isUpsell) {
      history.replace(routes.review());
      return null;
    }
    return (
      <div className={styles.container}>
        <div className={styles.inner}>
          <h1 className={styles.header}>Pictures from Previous School Years</h1>
          <div className={styles.emptyState}>
            {`We didn’t find any past pictures for ${student.firstName}. If you think this is a mistake, please contact us.`}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <ToastContainer position="bottom-left" />
      {isDiscountPopupOpen && (
        <DiscountPopup onClose={() => setIsDiscountPopupOpen(false)} />
      )}
      <div className={styles.inner}>
        <h1 className={styles.header}>Watch your child grow up</h1>
        <p className={styles.subheader}>Every year has its memories</p>
        <p className={styles.copy}>Order today before the school year ends</p>

        <div className={styles.grid}>
          {pastImages.map((studentImage) => (
            <PastImageOption
              key={studentImage.id}
              studentImage={studentImage}
              options={options}
              backgrounds={backgrounds}
              defaultBackground={defaultsByStudentImageId[studentImage.id]}
              selection={selections[studentImage.id]}
              onSelectOption={handleSelectOption}
              onSelectBackground={handleSelectBackground}
              packagePricelistId={studentImage.currentTypePackageId}
              onViewPackages={handleViewPackages}
            />
          ))}
        </div>
      </div>

      <div className={styles.footer}>
        <div className={styles.footerInner}>
          <div className={styles.summary}>
            <span className={styles.summaryCount}>
              {summary.count === 0
                ? 'No pictures selected'
                : `${summary.count} ${summary.count === 1 ? 'picture' : 'pictures'} selected`}
            </span>
            {summary.count > 0 && (
              <span className={styles.summaryTotal}>{`$${summary.total.toFixed(2)}`}</span>
            )}
            {volumeDiscountPercentage(summary.count) > 0 && (
              <span className={styles.savingsPill}>
                {`You'll save ${volumeDiscountPercentage(summary.count)}%`}
              </span>
            )}
          </div>
          {/* Only the save itself disables this. Nothing selected is a reason
              to leave, not a reason to be stuck: see handleContinue. */}
          <button
            type="button"
            className={styles.continueBtn}
            disabled={isSubmitting}
            onClick={handleContinue}
          >
            {isSubmitting ? 'Adding to cart...' : 'Continue to Checkout'}
          </button>
        </div>
        <div className={styles.bannerSlot}>{/* incentive banner slot */}</div>
      </div>
    </div>
  );
}
